import { AnimationEvent } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ComponentRef,
    EventEmitter,
    HostListener,
    Inject,
    NgModule,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import { getBuildIdQuery, IImage, ILink } from '@ncg/data';
import { TranslateModule } from '@ngx-translate/core';
import { IconSpriteModule } from 'ng-svg-icon-sprite';
import { Subject } from 'rxjs';
import { take, takeUntil } from 'rxjs/operators';
import { ConfiguratorTrimsEquipmentSidepanelComponent } from '../configurator';

import { SettingsService } from '../core/settings.service';
import { FormModule } from '../form/form.module';
import { ImpactOverlayRef } from '../overlay/overlay-ref';
import { ImpactOverlayLeave } from '../overlay/overlay.interfaces';
import { IMPACT_OVERLAY_DATA } from '../overlay/overlay.tokens';
import { RichTextModule } from '../rich-text/rich-text.module';
import { UmbracoGridModule } from '../umbraco-grid/umbraco-grid.module';
import { slideInLeft } from '../utils/animations/slide.animations';
import { HdAspect } from '../utils/helpers/aspect-ratio';
import { ImageUrl } from '../utils/helpers/image-helper';
import { SidePanelService } from './side-panel.service';
import { LazyContentAliases } from '../$lazy-content/models/lazy-content.models';
import { LazyContentModule } from '../$lazy-content/lazy-content.module';

@Component({
    selector: 'ncg-side-panel',
    template: `
        <aside
            [id]="dialogRef?.id"
            class="side-panel"
            role="dialog"
            [@slideContent]="animationState"
            (@slideContent.start)="onAnimationStart($event)"
            (@slideContent.done)="onAnimationDone($event)"
        >
            <button aria-label="Closes side panel dialog" class="button-icon is-primary side-panel__close" (click)="onClose()">
                <svg-icon-sprite
                    [src]="'assets/images/sprite-multi.svg' + buildIdQuery + '#close-circle'"
                    [width]="'58px'"
                    [height]="'58px'"
                    aria-hidden="true"
                ></svg-icon-sprite>
            </button>

            <div class="side-panel__content">
                <ng-container [ngSwitch]="data.component">
                    <ng-container *ngSwitchCase="'test-drive'">
                        <ng-container
                            *ngTemplateOutlet="
                                headerTmpl;
                                context: { title: 'sidepanel.test_drive_header', subtitle: 'sidepanel.test_drive_subtitle' }
                            "
                        ></ng-container>
                        <ncg-test-drive-form
                            [data]="data.componentData"
                            [isModelPage]="data.componentData.isModelPage"
                            [successMessage]="data.componentData.successMessage"
                            [usedCar]="data.componentData.usedCar"
                            [collectMarketingConsent]="data.componentData.collectMarketingConsent !== false"
                        >
                        </ncg-test-drive-form>
                    </ng-container>

                    <ng-container *ngSwitchCase="'offer'">
                        <ncg-offer-form
                            [isCashAllowed]="data.componentData.model?.isCashAllowed"
                            [isLoanAllowed]="data.componentData.model?.isLoanAllowed"
                            [isLeasingAllowed]="data.componentData.model?.isLeasingAllowed"
                            [model]="data.componentData.model"
                        ></ncg-offer-form>
                    </ng-container>

                    <ng-container *ngSwitchCase="'sidepanel'">
                        <div *ngIf="data.componentData" class="side-panel__content-template">
                            <ng-container
                                *ngTemplateOutlet="
                                    headerTmpl;
                                    context: {
                                        title: data.componentData.heading,
                                        subtitle: data.componentData.subheading
                                    }
                                "
                            ></ng-container>
                            <picture *ngIf="data.componentData.image" class="side-panel__content-template-image">
                                <source media="(min-width: 768px)" [srcset]="desktopSrcSet" />
                                <source media="(max-width: 767px)" [srcset]="tabletSrcSet" />
                                <img [src]="pictureSrc" [alt]="pictureAlt" />
                            </picture>
                            <ncg-rich-text [html]="data.componentData.text"></ncg-rich-text>
                            <div *ngIf="data?.componentData?.grid" class="side-panel__content-template-grid">
                                <ncg-umb-grid [allowNarrowContent]="true" [grid]="data.componentData.grid"></ncg-umb-grid>
                            </div>
                        </div>
                    </ng-container>

                    <ng-container *ngSwitchCase="'myprofile'">
                        <div *ngIf="data.componentData" class="side-panel__content-template">
                            <ng-container
                                *ngTemplateOutlet="
                                    headerTmpl;
                                    context: {
                                        title: data.componentData.heading,
                                        subtitle: data.componentData.subheading,
                                        icon: 'assets/images/sprite-multi.svg' + buildIdQuery + '#profile-user'
                                    }
                                "
                            ></ng-container>
                            <picture *ngIf="data.componentData.image" class="side-panel__content-template-image">
                                <source media="(min-width: 768px)" [srcset]="desktopSrcSet" />
                                <source media="(max-width: 767px)" [srcset]="tabletSrcSet" />
                                <img [src]="pictureSrc" [alt]="pictureAlt" />
                            </picture>
                            <ncg-rich-text [html]="data.componentData.text"></ncg-rich-text>
                            <div *ngIf="data?.componentData?.grid" class="side-panel__content-template-grid">
                                <ncg-umb-grid [allowNarrowContent]="true" [grid]="data.componentData.grid"></ncg-umb-grid>
                            </div>
                            <div *ngIf="myProfileExternalLink && myProfileExternalLink.url">
                                <a
                                    [href]="myProfileExternalLink.url"
                                    [attr.target]="myProfileExternalLink.target"
                                    class="button is-primary side-panel__btn-my-profile"
                                >
                                    <span>{{ myProfileExternalLink.name }}</span>
                                    <span class="icon">
                                        <svg-icon-sprite
                                            [src]="'assets/images/sprite-multi.svg' + buildIdQuery + '#link-external'"
                                            [viewBox]="'0 0 30 30'"
                                            [width]="'30px'"
                                            [height]="'30px'"
                                            aria-hidden="true"
                                        ></svg-icon-sprite>
                                    </span>
                                </a>
                            </div>
                        </div>
                    </ng-container>

                    <ng-container *ngSwitchCase="'trims-equipment'">
                        <ng-container
                            *ngTemplateOutlet="
                                headerTmpl;
                                context: {
                                    subtitle: 'configurator.side_panel_overview'
                                }
                            "
                        ></ng-container>
                        <!-- TODO: Probably do this differently so we don't have to import it in here -->
                        <ncg-configurator-trims-equipment-sidepanel
                            [model]="data.componentData.model"
                            [capacity]="data.componentData.capacity"
                            [selectedTrimId]="data.componentData.selectedTrimId"
                        ></ncg-configurator-trims-equipment-sidepanel>
                    </ng-container>

                    <ng-container *ngSwitchCase="'service-booking-service'">
                        <ng-template ncgLazyComponentLoader [alias]="serviceBookingServiceAlias" [data]="data.componentData"></ng-template>
                    </ng-container>
                </ng-container>
            </div>

            <ng-template #headerTmpl let-title="title" let-subtitle="subtitle" let-icon="icon">
                <header class="aside-header" *ngIf="title || subtitle">
                    <h1 *ngIf="title" class="aside-header__title">
                        <span *ngIf="icon" class="icon">
                            <svg-icon-sprite
                                [src]="icon"
                                [viewBox]="'0 0 30 30'"
                                [width]="'30px'"
                                [height]="'30px'"
                                aria-hidden="true"
                            ></svg-icon-sprite>
                        </span>
                        {{ title | translate }}
                    </h1>
                    <h2 *ngIf="subtitle" class="aside-header__side-panel-title">
                        {{ subtitle | translate }}
                    </h2>
                </header>
            </ng-template>
        </aside>
    `,
    animations: [slideInLeft],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SidePanelComponent implements ImpactOverlayLeave, OnInit, OnDestroy {
    componentRef: ComponentRef<any>;
    animationState: 'void' | 'enter' | 'leave' = 'enter';
    animationStateChanged = new EventEmitter<AnimationEvent>();
    pictureSrc = '';
    pictureAlt = '';
    tabletSrcSet: string = '';
    desktopSrcSet: string = '';
    buildIdQuery = getBuildIdQuery();
    myProfileExternalLink?: ILink;
    serviceBookingServiceAlias = LazyContentAliases.ServiceBookingServiceDialog;

    private readonly unsubscribe = new Subject<void>();

    constructor(
        public readonly dialogRef: ImpactOverlayRef,
        @Inject(IMPACT_OVERLAY_DATA) public readonly data: any,
        private readonly cd: ChangeDetectorRef,
        private readonly router: Router,
        private readonly settingsService: SettingsService,
        private readonly sidePanelService: SidePanelService
    ) {}

    ngOnInit() {
        this.sidePanelService.toggleIsSidePanel(true);

        // Close sidepanel on navigation
        this.router.events.pipe(take(1), takeUntil(this.unsubscribe)).subscribe(() => {
            if (this.dialogRef?.componentInstance) {
                this.onClose();
            }
        });

        if (this.data.component === 'sidepanel') {
            if (this.data.componentData.image) {
                this.generateImagesSrcSets(this.data.componentData.image);
                this.pictureAlt = this.data.componentData.image.altText || '';
            }
        }

        if (this.data.component === 'myprofile') {
            this.settingsService
                .get()
                .pipe(take(1), takeUntil(this.unsubscribe))
                .subscribe((settings) => {
                    this.myProfileExternalLink = settings.seezSetup.myProfileLink;
                });
        }
    }

    ngOnDestroy() {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }

    onAnimationStart(event: AnimationEvent) {
        this.animationStateChanged.emit(event);
    }

    onAnimationDone(event: AnimationEvent) {
        this.animationStateChanged.emit(event);
    }

    startExitAnimation() {
        this.animationState = 'leave';
        this.cd.detectChanges();
    }

    @HostListener('window:keyup.esc', ['$event'])
    onClose() {
        this.sidePanelService.toggleIsSidePanel(false);
        this.dialogRef.close();
    }

    generateImagesSrcSets(image: IImage) {
        this.tabletSrcSet = `
            ${ImageUrl(image, { width: 683, heightratio: HdAspect, mode: 'crop' })} 1x,
            ${ImageUrl(image, { width: 1366, heightratio: HdAspect, mode: 'crop' })} 2x
            `;
        this.desktopSrcSet = `
            ${ImageUrl(image, { width: 510, heightratio: HdAspect, mode: 'crop' })} 1x,
            ${ImageUrl(image, { width: 1020, heightratio: HdAspect, mode: 'crop' })} 2x
            `;
        this.pictureSrc = ImageUrl(image, { width: 1550, heightratio: HdAspect, mode: 'crop' });
    }
}

@NgModule({
    imports: [
        CommonModule,
        FormModule,
        UmbracoGridModule,
        RichTextModule,
        TranslateModule.forChild(),
        IconSpriteModule,
        ConfiguratorTrimsEquipmentSidepanelComponent,
        LazyContentModule,
    ],
    declarations: [SidePanelComponent],
})
export class SidePanelComponentModule {}
